import React, { useState } from 'react';
import { ComposedChart, Area, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, PieChart, Pie, Cell, Sector } from 'recharts';
import { TrendingUp } from 'lucide-react';
import { formatCurrencyAmount, formatNumberValue, formatPercentageValue, sentenceCase } from '../../../../utils/miscFunctions';
import DashboardTabs from '../../../../components/dashboard/tabs/DashboardTabs';


// Color palette for donut charts
const DONUT_CHART_COLORS = [
  '#4F46E5', // indigo-600
  '#10B981', // emerald-500
  '#F59E0B', // amber-500
  '#EF4444', // red-500
  '#8B5CF6', // violet-500
  '#EC4899', // pink-500
  '#14B8A6', // teal-500
  '#F97316', // orange-500
  '#6366F1', // indigo-400
  '#06B6D4', // cyan-500
];

type DefaultingRateMetrics = {
  defaultingLoans: number;
  defaultingRate: number;
  overduePayments: number;
  totalLoans: number;
}
interface PaymentHistoryChartProps {
  labels: string[];
  loans: number[];
  paidRepaymentData: number[];
  expectedRepaymentData: number[];
  productData: any;
  trendData: any;
  metricsSummary: any;
  chartOption1: string;
  chartOption2: string;
  defaultingRateMetrics: DefaultingRateMetrics;
}

interface ChartData {
  name: string;
  expectedRepaymentData: number;
  paidRepaymentData: number;

}

const PaymentHistoryChart: React.FC<PaymentHistoryChartProps> = ({ labels, loans, paidRepaymentData, expectedRepaymentData, productData, trendData, metricsSummary, chartOption1, chartOption2, defaultingRateMetrics }) => {

  // Format the data for Recharts
  const barChartData: ChartData[] = labels?.map((month, index) => {
    const expectedRepaymentValue = typeof expectedRepaymentData?.[index] === 'number' ? expectedRepaymentData[index] : 0;
    const paidRepaymentValue = typeof paidRepaymentData?.[index] === 'number' ? paidRepaymentData[index] : 0;

    return {
      name: month,
      expectedRepaymentData: expectedRepaymentValue,
      paidRepaymentData: paidRepaymentValue,
    };
  });

  // Get the first matching series for each type
  const expectedRepaymentAmountsTrend = trendData?.series?.find((item: any) => item.name === 'Expected Repayments Amounts')?.data || [];
  const paidRepaymentAmountsTrend = trendData?.series?.find((item: any) => item.name === 'Paid Repayments Amounts')?.data || [];

  const trendChartData: ChartData[] = trendData?.labels?.map((month: string, index: number) => ({
    name: month,
    expectedRepaymentData: expectedRepaymentAmountsTrend?.[index] || 0,
    paidRepaymentData: paidRepaymentAmountsTrend?.[index] || 0,
  })) || [];

  // Pie chart data
  const pieData: any = {
    amount: {
      'Motor Insurance': productData?.amount?.MOTOR_INSURANCE_POLICY,
      'Premium Financing': productData?.amount?.PREMIUM_FINANCING,
    },
    count: {
      'Motor Insurance': productData?.count?.MOTOR_INSURANCE_POLICY,
      'Premium Financing': productData?.count?.PREMIUM_FINANCING,
    }
  };

  // State for active payment type in the pie chart
  const [activePaymentIndex, setActivePaymentIndex] = useState<number | undefined>(undefined);


  // Custom tooltip for the chart
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="p-3 bg-white dark:bg-darkBg dark:border-darkBg-border shadow-lg rounded-md border border-gray-100">
          <p className="font-medium text-sm text-gray-900 dark:text-darkBg-mainText">{label}</p>
          <div>
            {payload.map((entry: any, index: number) => (
              <p key={`item-${index}`} className="text-sm" style={{ color: entry.color }}>
                {entry.name}: {formatCurrencyAmount(entry.value)}
              </p>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  // Define interface for active shape props
  interface ActiveShapeProps {
    cx: number;
    cy: number;
    innerRadius: number;
    outerRadius: number;
    startAngle: number;
    endAngle: number;
    fill: string;
    payload: {
      name: string;
      value: number;
      color: string;
    };
    percent: number;
    value: number;
  }

  // Render pie chart function
  const renderPieChart = (data: any, name: string) => {
    if (!data) return <div className="h-full flex items-center justify-center text-gray-500 dark:text-darkBg-subText">No data available</div>;

    const chartData = Object.entries(data).map(([name, value]) => ({
      name,
      value: Number(value) || 0,
      color: DONUT_CHART_COLORS[Object.keys(data).indexOf(name) % DONUT_CHART_COLORS.length]
    }));

    const renderActiveShape = ({
      cx,
      cy,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill,
      payload,
      percent,
      value
    }: ActiveShapeProps) => {

      return (
        <g>
          <text x={cx} y={cy} dy={-10} textAnchor="middle" fill="#333" className="text-sm font-medium dark:fill-darkBg-mainText">
            {name}
          </text>
          <text x={cx} y={cy} dy={15} textAnchor="middle" fill="#000" className="text-lg font-bold dark:fill-darkBg-mainText">
            {name === 'Amount' ? formatCurrencyAmount(value) : value}
          </text>
          <Sector
            cx={cx}
            cy={cy}
            innerRadius={innerRadius}
            outerRadius={outerRadius + 5}
            startAngle={startAngle}
            endAngle={endAngle}
            fill={fill}
          />
          <Sector
            cx={cx}
            cy={cy}
            startAngle={startAngle}
            endAngle={endAngle}
            innerRadius={outerRadius + 8}
            outerRadius={outerRadius + 10}
            fill={fill}
          />
        </g>
      );
    };

    return (
      <PieChart>
        <Pie
          activeIndex={activePaymentIndex}
          activeShape={renderActiveShape as any}
          data={chartData}
          cx="50%"
          cy="50%"
          innerRadius={50}
          outerRadius={80}
          paddingAngle={2}
          dataKey="value"
          onMouseEnter={(_, index) => setActivePaymentIndex(index)}
          onMouseLeave={() => setActivePaymentIndex(-1)}
        >
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color} />
          ))}
        </Pie>
        <Tooltip
          formatter={(value: number) => name === 'Amount' ? formatCurrencyAmount(value) : value}
        />
        <Legend
          layout="horizontal"
          verticalAlign="bottom"
          align="center"
          formatter={(value) => <span className="text-xs text-gray-700 dark:text-darkBg-subText">{value}</span>}
        />
      </PieChart>
    );
  };

  // Chart type tabs
  const [chartType, setChartType] = useState<'composed' | 'bars' | 'area'>('bars');

  let monthlyAverageTotal = 0;
  let monthlyAverageAmount = 0;
  let monthlyTotalCount = 0;

  for (const month in metricsSummary?.monthlyAverageLoan) {
    monthlyAverageTotal += metricsSummary?.monthlyAverageLoan[month].totalAmount;
    monthlyAverageAmount += metricsSummary?.monthlyAverageLoan[month].averageAmount;
    monthlyTotalCount += metricsSummary?.monthlyAverageLoan[month].count;
  }

  return (
    <div className="bg-white dark:bg-darkBg shadow rounded-lg p-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 ">
        <div className="lg:col-span-2  ">
          {/* Chart Type Tabs */}
          <div className="flex items-center gap-6 mb-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText">Monthly Repayments</h3>
            <div className="flex items-center space-x-2 bg-gray-100 dark:bg-darkBg-border p-1 rounded-md">
              <button
                className={`px-3 py-1 text-sm font-medium rounded-md ${chartType === 'bars' ? 'bg-[#FBC02D] shadow' : ''}`}
                onClick={() => setChartType('bars')}
              >
                Comparison
              </button>
              <button
                className={`px-3 py-1 text-sm font-medium rounded-md ${chartType === 'area' ? 'bg-[#FBC02D] shadow' : ''}`}
                onClick={() => setChartType('area')}
              >
                Trends
              </button>
            </div>
          </div>

          {/* Chart */}
          <div className="h-60 md:h-80 lg:h-96 lg:max-h-96">
            <ResponsiveContainer width="100%" height="100%">
              {(() => {
                switch (chartType) {
                  case 'composed':
                    return (
                      <ComposedChart
                        data={barChartData}
                        margin={{
                          top: 20,
                          right: 20,
                          bottom: 20,
                          left: 20,
                        }}
                        className='text-xs'
                        layout='vertical'>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.6} />
                        <XAxis dataKey="name" />
                        <YAxis tickFormatter={(value) => value} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Bar dataKey="expectedRepaymentData" fill="#3b82f6" name={`${chartOption2}`} barSize={20} />
                        <Bar dataKey="paidRepaymentData" fill="#ef4444" name={`${chartOption1}`} barSize={20} />
                      </ComposedChart>
                    );
                  case 'bars':
                    return (
                      <BarChart
                        data={barChartData}
                        margin={{
                          top: 20,
                          right: 20,
                          bottom: 20,
                          left: 20,
                        }}
                        className='text-xs'
                      >
                        <CartesianGrid strokeDasharray="3 3" opacity={0.6} />
                        <XAxis dataKey="name" tickFormatter={(value) => sentenceCase(value.substring(0, 3))} />
                        <YAxis tickFormatter={(value) => formatCurrencyAmount(value)} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Bar dataKey="expectedRepaymentData" fill="#3b82f6" name={`${chartOption1}`} />
                        <Bar dataKey="paidRepaymentData" fill="#ef4444" name={`${chartOption2}`} />
                      </BarChart>
                    );
                  case 'area':
                    return (
                      <ComposedChart
                        data={trendChartData}
                        margin={{
                          top: 20,
                          right: 20,
                          bottom: 20,
                          left: 20,
                        }}
                        className='text-xs'
                      >
                        <CartesianGrid strokeDasharray="3 3" opacity={0.6} />
                        <XAxis dataKey="name" tickFormatter={(value) => sentenceCase(value.substring(0, 3))} />
                        <YAxis tickFormatter={(value) => formatCurrencyAmount(value)} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Area
                          type="monotone"
                          dataKey="expectedRepaymentData"
                          stroke="#3b82f6"
                          fill="#3b82f6"
                          fillOpacity={0.3}
                          name={`${chartOption1}`}
                          stackId="1"
                          strokeWidth={2}
                          dot={{ r: 2 }}
                          activeDot={{ r: 4 }}
                        />
                        <Area
                          type="monotone"
                          dataKey="paidRepaymentData"
                          stroke="#ef4444"
                          fill="#ef4444"
                          fillOpacity={0.3}
                          name={`${chartOption2}`}
                          stackId="2"
                          strokeWidth={2}
                          dot={{ r: 2 }}
                          activeDot={{ r: 4 }}
                        />
                      </ComposedChart>
                    );
                  default:
                    // Fallback to composed chart as default
                    return (
                      <ComposedChart
                        data={barChartData}
                        margin={{
                          top: 20,
                          right: 20,
                          bottom: 20,
                          left: 20,
                        }}
                        className='text-xs'
                      >
                        <CartesianGrid strokeDasharray="3 3" opacity={0.6} />
                        <XAxis dataKey="name" tickFormatter={(value) => sentenceCase(value.substring(0, 3))} />
                        <YAxis tickFormatter={(value) => formatCurrencyAmount(value)} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Bar dataKey="disbursementData" fill="#3b82f6" name="Disbursements" barSize={20} />
                        <Bar dataKey="loanData" fill="#ef4444" name="Loans" barSize={20} />
                      </ComposedChart>
                    );
                }
              })()}
            </ResponsiveContainer>
          </div>

        </div>

        {/* Summary Stats - Takes 1 column */}
        <div className="lg:col-span-1">
          <div className="h-60 mb-6">
            <DashboardTabs
              tabGroupTitle="Products"
              titleClassName="text-lg font-medium text-gray-900 dark:text-darkBg-mainText"
              tab1={{
                name: 'Amount',
                children: (
                  <ResponsiveContainer width="100%" height="100%">
                    {renderPieChart(pieData?.amount, 'Amount')}
                  </ResponsiveContainer>
                )
              }}
              tab2={{
                name: 'Count',
                children: (
                  <ResponsiveContainer width="100%" height="100%">
                    {renderPieChart(pieData?.count, 'Count')}
                  </ResponsiveContainer>
                )
              }}
              activeTab={'Amount'}
            />
          </div>

          <div className="pt-10 space-y-3">
            <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-100 dark:border-blue-800">
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <TrendingUp className="h-5 w-5 text-blue-500 mr-2" />
                  <span className="text-sm font-medium text-gray-700 dark:text-darkBg-subText">Default Rate</span>
                </div>
                <span className="text-sm font-semibold text-red-600 dark:text-red-400">
                  {formatPercentageValue(defaultingRateMetrics?.defaultingRate)}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                <div className="text-xs text-gray-500 dark:text-darkBg-subText mb-1">Defaulted Payments</div>
                <div className="text-sm font-semibold">{formatNumberValue(defaultingRateMetrics?.defaultingLoans)}</div>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                <div className="text-xs text-gray-500 dark:text-darkBg-subText mb-1">Total Payments</div>
                <div className="text-sm font-semibold">{formatCurrencyAmount(metricsSummary?.totalLoanAmount)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentHistoryChart;
